import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Results = () => {
  // Define the success images (success1.webp through success11.webp)
  const successImages = Array(11).fill(null).map((_, index) => ({
    id: index + 1,
    image: `/success${index + 1}.webp`,
    description: `Client transformation #${index + 1}`
  }));

  const [currentResult, setCurrentResult] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  // Auto-rotate images every second
  useEffect(() => {
    let interval;
    
    if (autoPlay) {
      interval = setInterval(() => {
        setCurrentResult((prev) => (prev + 1) % successImages.length);
      }, 2000); // Change image every 2 seconds
    }
    
    // Clear interval on unmount or when autoplay is disabled
    return () => clearInterval(interval);
  }, [autoPlay, successImages.length]);

  // Pause auto-rotation when user interacts with navigation
  const handleNavigation = (index) => {
    setAutoPlay(false); // Stop auto-rotation
    setCurrentResult(index);
    
    // Resume auto-rotation after 10 seconds of inactivity
    const timer = setTimeout(() => {
      setAutoPlay(true);
    }, 10000);
    
    return () => clearTimeout(timer);
  };

  const goToPrevious = () => {
    const newIndex = currentResult === 0 ? successImages.length - 1 : currentResult - 1;
    handleNavigation(newIndex);
  };

  const goToNext = () => {
    const newIndex = (currentResult + 1) % successImages.length;
    handleNavigation(newIndex);
  };

  return (
    <section className="py-16 bg-white" id="results">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
            Results We've <span className="text-sky-600">Helped People</span> Achieve
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real transformations from real clients
          </p>
        </div>

        <div className="relative">
          {/* Left navigation arrow */}
          <button
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-sky-100 hover:bg-sky-200 p-2 rounded-full text-sky-600 z-10 hidden md:block"
            onClick={goToPrevious}
            aria-label="Previous transformation"
          >
            <ChevronLeft size={24} />
          </button>

          {/* Current transformation - single image containing before/after */}
          <div className="flex justify-center">
            <div className="w-full max-w-3xl mx-auto">
              <div className="bg-sky-50 rounded-xl p-4">
                <img 
                  src={successImages[currentResult].image} 
                  alt={successImages[currentResult].description} 
                  className="w-full h-auto rounded-lg mx-auto"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `/api/placeholder/800/500?text=Transformation ${currentResult + 1}`;
                  }}
                />
              </div>
            </div>
          </div>

          {/* Right navigation arrow */}
          <button
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-sky-100 hover:bg-sky-200 p-2 rounded-full text-sky-600 z-10 hidden md:block"
            onClick={goToNext}
            aria-label="Next transformation"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Navigation dots */}
        <div className="flex justify-center mt-8 space-x-2">
          {successImages.map((_, index) => (
            <button
              key={index}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentResult ? 'bg-sky-600 scale-125' : 'bg-sky-200 hover:bg-sky-300'
              }`}
              onClick={() => handleNavigation(index)}
              aria-label={`View transformation ${index + 1}`}
            />
          ))}
        </div>

        <div className="mt-16 p-8 bg-sky-50 rounded-xl text-center">
          <h3 className="text-2xl font-bold mb-6 text-gray-900">
            Our Tested and Proven System 
            <span className="text-sky-600"> Works For You</span>
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="font-bold text-lg mb-3 text-sky-600">ACHIEVE YOUR DREAM BODY!</h4>
              <p className="text-gray-700">
                I have a tested and proven system for helping people to achieve success. This system gives you a roadmap that we will follow to reach your goals.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="font-bold text-lg mb-3 text-sky-600">ACHIEVE YOUR RESULTS FASTER!</h4>
              <p className="text-gray-700">
                Based on my background as a Doctor of Physical Therapy I have a scientific understanding of how the body works and the best possible way to achieve these outcomes.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="font-bold text-lg mb-3 text-sky-600">DON'T LET INJURIES SLOW YOU DOWN!</h4>
              <p className="text-gray-700">
                As a Doctor of Physical Therapy I am able to work with medical conditions and create the best possible plan for success based on your unique situation.
              </p>
            </div>
          </div>
          
          <a
            href="#packages"
            className="inline-block bg-sky-600 text-white text-lg px-8 py-4 rounded-xl hover:bg-sky-700 transition duration-300 shadow-lg"
          >
            Click For Pricing Info
          </a>
        </div>
      </div>
    </section>
  );
};

export default Results;